/**
 * errorContract.ts
 * ----------------
 * Parses the backend's "wrapped" error envelope into a normalized shape the
 * client can branch on. Clients branch on:
 *   - messageCode        : HTTP status as string ("400","401","500","503")
 *   - messageDescription : stable application error code (branch on this)
 *   - messageDetails     : human-readable only (never branch on this)
 *   - correlationId      : surfaced in modals/toasts for support
 *
 * SAFETY / BACKWARD COMPATIBILITY:
 * parseApiError returns `null` unless the response actually contains the wrapped
 * `messages` envelope. Any non-wrapped error (network error, legacy response,
 * plain status with no envelope) yields `null`, so the orchestrator returns
 * PASS and the existing interceptor behavior runs unchanged. This lets the
 * client ship before the backend contract is live everywhere.
 */
export interface ApiMessage {
  messageType?: string;
  messageDescription?: string;
  messageCode?: string;
  messageDetails?: string;
  correlationId?: string;
}

export interface ParsedAuthError {
  messageCode: string;
  messageDescription: string;
  messageDetails: string;
  correlationId?: string;
}

export function parseApiError(error: any): ParsedAuthError | null {
  const httpStatus = error?.response?.status ?? 0;
  const body = error?.response?.data;

  // Only engage the new contract when a wrapped `messages` envelope is present.
  const messages: ApiMessage[] | null = Array.isArray(body?.messages)
    ? body.messages
    : null;
  if (!messages || messages.length === 0) {
    return null;
  }

  const msg: ApiMessage | undefined =
    messages.find((m) => m.messageType === 'ERROR') ?? messages[0];

  // Require at least a code or a description to treat this as a contract error.
  if (!msg || (!msg.messageCode && !msg.messageDescription)) {
    return null;
  }

  return {
    messageCode: String(msg.messageCode ?? httpStatus ?? ''),
    messageDescription: msg.messageDescription ?? '',
    messageDetails: msg.messageDetails ?? '',
    correlationId: msg.correlationId,
  };
}
